import IAction from "../../types/IAction";
import { readMessageBack } from "../../utils/readMessageBack";
import actionTypes from "../actionTypes";
import { Archivo } from "../components/expedientsV2controls/types";

export interface PersonaReferencia {
  idPersona: number;
  nombreCompleto: string;
  curp: string;
  curpAprobada: boolean;
  appCorreo: string;
  appCorreoAprobado: boolean;
  tipo: string;
  asignadoA: number;
  fechaCreacion: string;
  estatusMembresia: string;
  estatus: string;
  totalPass: boolean;
  actividadCount: number;
  clasePersonalizadaCount: number;
  programaDeportivoCount: number;
  ventasCount: number;
  invitadoCount: number;
  invitadoPersonaTutorCount: number;
  titularCount: number;
  propietarioCount: number;
  membresiaSocioPersonaCount: number;
  productosInstanciasCount: number;
  prospectoCount: number;
  resolucion: boolean;
  razonesNoLiberable: string[];
}

export interface VerificacionDocumentoResponse {
  resolucion: boolean;
  razonamiento: string;
  porcentajeAceptacion: number;
  nombreCoincide: boolean;
  fechaNacimientoCoincide: boolean;
  esPasaporte: boolean;
  esCarnetExtranjeria: boolean;
  documentoManipulado: boolean;
}

export interface GetControversiaCurp {
  controversiaCurp: PersonaReferencia;
  gettingControversiaCurp: boolean;
  gettingControversiaCurpError: string;
}

export interface PutLiberarCurp {
  puttingLiberarCurp: boolean;
  puttingLiberarCurpError: string;
}

export interface PutHomologarPropietario {
  puttingHomologarPropietario: boolean;
  puttingHomologarPropietarioError: string;
}

export interface PutHomologarTitularMayor {
  puttingHomologarTitularMayor: boolean;
  puttingHomologarTitularMayorError: string;
}

export interface PostVerificarExtranjero {
  verificacionExtranjero: VerificacionDocumentoResponse;
  postingVerificarExtranjero: boolean;
  postingVerificarExtranjeroError: string;
}

export interface PostVerificarDocumentoTitular {
  verificacionDocumentoTitular: VerificacionDocumentoResponse;
  postingVerificarDocumentoTitular: boolean;
  postingVerificarDocumentoTitularError: string;
}

export interface PostVerificarDocumentoIntegrante {
  verificacionDocumentoIntegrante: VerificacionDocumentoResponse;
  postingVerificarDocumentoIntegrante: boolean;
  postingVerificarDocumentoIntegranteError: string;
}

export interface PropietarioDuplicado {
  idPersonaCurp: number;
  nombreCurp: string;
  aceptable: boolean;
  levenshtein: number;
  error?: string;
}

export interface GetVerificarPropietarioDuplicado {
  propietarioDuplicado: PropietarioDuplicado;
  gettingPropietarioDuplicado: boolean;
  gettingPropietarioDuplicadoError: string;
}

export interface GetIdentificacionTitularExtranjero {
  identificacionTitularExtranjero: Archivo;
  gettingIdentificacionTitularExtranjero: boolean;
  gettingIdentificacionTitularExtranjeroError: string;
}

export interface GetIdentificacionIntegranteExtranjero {
  identificacionIntegranteExtranjero: Archivo;
  gettingIdentificacionIntegranteExtranjero: boolean;
  gettingIdentificacionIntegranteExtranjeroError: string;
}

interface State
  extends GetControversiaCurp,
    PutLiberarCurp,
    PutHomologarPropietario,
    PutHomologarTitularMayor,
    PostVerificarExtranjero,
    PostVerificarDocumentoTitular,
    PostVerificarDocumentoIntegrante,
    GetVerificarPropietarioDuplicado,
    GetIdentificacionTitularExtranjero,
    GetIdentificacionIntegranteExtranjero {}

const initialState: State = {
  controversiaCurp: null,
  gettingControversiaCurp: false,
  gettingControversiaCurpError: null,

  puttingLiberarCurp: false,
  puttingLiberarCurpError: null,

  puttingHomologarPropietario: false,
  puttingHomologarPropietarioError: null,

  puttingHomologarTitularMayor: false,
  puttingHomologarTitularMayorError: null,

  verificacionExtranjero: null,
  postingVerificarExtranjero: false,
  postingVerificarExtranjeroError: null,

  verificacionDocumentoTitular: null,
  postingVerificarDocumentoTitular: false,
  postingVerificarDocumentoTitularError: null,

  verificacionDocumentoIntegrante: null,
  postingVerificarDocumentoIntegrante: false,
  postingVerificarDocumentoIntegranteError: null,

  propietarioDuplicado: null,
  gettingPropietarioDuplicado: false,
  gettingPropietarioDuplicadoError: null,

  identificacionTitularExtranjero: null,
  gettingIdentificacionTitularExtranjero: false,
  gettingIdentificacionTitularExtranjeroError: null,

  identificacionIntegranteExtranjero: null,
  gettingIdentificacionIntegranteExtranjero: false,
  gettingIdentificacionIntegranteExtranjeroError: null
};

export default function personaReferenciaReducer(
  state: State = initialState,
  action: IAction
): State {
  const snapshot = snapshotAction(state, action);

  // Llamada a resolver de snapshot
  state != snapshot &&
    !action.type.endsWith("_PENDING") &&
    action.meta &&
    action.meta.resolver &&
    action.meta.resolver({
      fulfilled: action.type.endsWith("_FULFILLED"),
      ...snapshot
    });

  return state != snapshot
    ? {
        ...state,
        ...snapshot
      }
    : state;
}

function snapshotAction(state: State = initialState, action: IAction) {
  switch (action.type) {
    // GET_CONTROVERSIA_CURP
    case `${actionTypes.GET_CONTROVERSIA_CURP}_PENDING`: {
      return {
        gettingControversiaCurp: true,
        gettingControversiaCurpError: null
      };
    }
    case `${actionTypes.GET_CONTROVERSIA_CURP}_REJECTED`: {
      return {
        controversiaCurp: null,
        gettingControversiaCurp: false,
        gettingControversiaCurpError: readMessageBack(action)
      };
    }
    case `${actionTypes.GET_CONTROVERSIA_CURP}_FULFILLED`: {
      return {
        controversiaCurp: action.payload.data,
        gettingControversiaCurp: false,
        gettingControversiaCurpError: null
      };
    }

    // PUT_LIBERAR_CURP
    case `${actionTypes.PUT_LIBERAR_CURP}_PENDING`: {
      return {
        puttingLiberarCurp: true,
        puttingLiberarCurpError: null
      };
    }
    case `${actionTypes.PUT_LIBERAR_CURP}_REJECTED`: {
      return {
        puttingLiberarCurp: false,
        puttingLiberarCurpError: readMessageBack(action)
      };
    }
    case `${actionTypes.PUT_LIBERAR_CURP}_FULFILLED`: {
      return {
        puttingLiberarCurp: false,
        puttingLiberarCurpError: null
      };
    }

    // PUT_HOMOLOGAR_PROPIETARIO
    case `${actionTypes.PUT_HOMOLOGAR_PROPIETARIO}_PENDING`: {
      return {
        puttingHomologarPropietario: true,
        puttingHomologarPropietarioError: null
      };
    }
    case `${actionTypes.PUT_HOMOLOGAR_PROPIETARIO}_REJECTED`: {
      return {
        puttingHomologarPropietario: false,
        puttingHomologarPropietarioError: readMessageBack(action)
      };
    }
    case `${actionTypes.PUT_HOMOLOGAR_PROPIETARIO}_FULFILLED`: {
      return {
        puttingHomologarPropietario: false,
        puttingHomologarPropietarioError: null
      };
    }

    // PUT_HOMOLOGAR_TITULAR_MAYOR
    case `${actionTypes.PUT_HOMOLOGAR_TITULAR_MAYOR}_PENDING`: {
      return {
        puttingHomologarTitularMayor: true,
        puttingHomologarTitularMayorError: null
      };
    }
    case `${actionTypes.PUT_HOMOLOGAR_TITULAR_MAYOR}_REJECTED`: {
      return {
        puttingHomologarTitularMayor: false,
        puttingHomologarTitularMayorError: readMessageBack(action)
      };
    }
    case `${actionTypes.PUT_HOMOLOGAR_TITULAR_MAYOR}_FULFILLED`: {
      return {
        puttingHomologarTitularMayor: false,
        puttingHomologarTitularMayorError: null
      };
    }

    // POST_VERIFICAR_EXTRANJERO
    case `${actionTypes.POST_VERIFICAR_EXTRANJERO}_PENDING`: {
      return {
        postingVerificarExtranjero: true,
        postingVerificarExtranjeroError: null
      };
    }
    case `${actionTypes.POST_VERIFICAR_EXTRANJERO}_REJECTED`: {
      return {
        verificacionExtranjero: null,
        postingVerificarExtranjero: false,
        postingVerificarExtranjeroError: readMessageBack(action)
      };
    }
    case `${actionTypes.POST_VERIFICAR_EXTRANJERO}_FULFILLED`: {
      return {
        verificacionExtranjero: action.payload.data,
        postingVerificarExtranjero: false,
        postingVerificarExtranjeroError: null
      };
    }

    // POST_VERIFICAR_DOCUMENTO_TITULAR
    case `${actionTypes.POST_VERIFICAR_DOCUMENTO_TITULAR}_PENDING`: {
      return {
        postingVerificarDocumentoTitular: true,
        postingVerificarDocumentoTitularError: null
      };
    }
    case `${actionTypes.POST_VERIFICAR_DOCUMENTO_TITULAR}_REJECTED`: {
      return {
        verificacionDocumentoTitular: null,
        postingVerificarDocumentoTitular: false,
        postingVerificarDocumentoTitularError: readMessageBack(action)
      };
    }
    case `${actionTypes.POST_VERIFICAR_DOCUMENTO_TITULAR}_FULFILLED`: {
      return {
        verificacionDocumentoTitular: action.payload.data,
        postingVerificarDocumentoTitular: false,
        postingVerificarDocumentoTitularError: null
      };
    }

    // POST_VERIFICAR_DOCUMENTO_INTEGRANTE
    case `${actionTypes.POST_VERIFICAR_DOCUMENTO_INTEGRANTE}_PENDING`: {
      return {
        postingVerificarDocumentoIntegrante: true,
        postingVerificarDocumentoIntegranteError: null
      };
    }
    case `${actionTypes.POST_VERIFICAR_DOCUMENTO_INTEGRANTE}_REJECTED`: {
      return {
        verificacionDocumentoIntegrante: null,
        postingVerificarDocumentoIntegrante: false,
        postingVerificarDocumentoIntegranteError: readMessageBack(action)
      };
    }
    case `${actionTypes.POST_VERIFICAR_DOCUMENTO_INTEGRANTE}_FULFILLED`: {
      return {
        verificacionDocumentoIntegrante: action.payload.data,
        postingVerificarDocumentoIntegrante: false,
        postingVerificarDocumentoIntegranteError: null
      };
    }

    // GET_VERIFICAR_PROPIETARIO_DUPLICADO
    case `${actionTypes.GET_VERIFICAR_PROPIETARIO_DUPLICADO}_PENDING`: {
      return {
        gettingPropietarioDuplicado: true,
        gettingPropietarioDuplicadoError: null
      };
    }
    case `${actionTypes.GET_VERIFICAR_PROPIETARIO_DUPLICADO}_REJECTED`: {
      return {
        propietarioDuplicado: null,
        gettingPropietarioDuplicado: false,
        gettingPropietarioDuplicadoError: readMessageBack(action)
      };
    }
    case `${actionTypes.GET_VERIFICAR_PROPIETARIO_DUPLICADO}_FULFILLED`: {
      return {
        propietarioDuplicado: action.payload.data,
        gettingPropietarioDuplicado: false,
        gettingPropietarioDuplicadoError: null
      };
    }

    // GET_IDENTIFICACION_TITULAR_EXTRANJERO
    case `${actionTypes.GET_IDENTIFICACION_TITULAR_EXTRANJERO}_PENDING`: {
      return {
        gettingIdentificacionTitularExtranjero: true,
        gettingIdentificacionTitularExtranjeroError: null
      };
    }
    case `${actionTypes.GET_IDENTIFICACION_TITULAR_EXTRANJERO}_REJECTED`: {
      return {
        identificacionTitularExtranjero: null,
        gettingIdentificacionTitularExtranjero: false,
        gettingIdentificacionTitularExtranjeroError: readMessageBack(action)
      };
    }
    case `${actionTypes.GET_IDENTIFICACION_TITULAR_EXTRANJERO}_FULFILLED`: {
      return {
        identificacionTitularExtranjero: action.payload.data,
        gettingIdentificacionTitularExtranjero: false,
        gettingIdentificacionTitularExtranjeroError: null
      };
    }

    // GET_IDENTIFICACION_INTEGRANTE_EXTRANJERO
    case `${actionTypes.GET_IDENTIFICACION_INTEGRANTE_EXTRANJERO}_PENDING`: {
      return {
        gettingIdentificacionIntegranteExtranjero: true,
        gettingIdentificacionIntegranteExtranjeroError: null
      };
    }
    case `${actionTypes.GET_IDENTIFICACION_INTEGRANTE_EXTRANJERO}_REJECTED`: {
      return {
        identificacionIntegranteExtranjero: null,
        gettingIdentificacionIntegranteExtranjero: false,
        gettingIdentificacionIntegranteExtranjeroError: readMessageBack(action)
      };
    }
    case `${actionTypes.GET_IDENTIFICACION_INTEGRANTE_EXTRANJERO}_FULFILLED`: {
      return {
        identificacionIntegranteExtranjero: action.payload.data,
        gettingIdentificacionIntegranteExtranjero: false,
        gettingIdentificacionIntegranteExtranjeroError: null
      };
    }

    // CLEAR_PERSONA_REFERENCIA
    case actionTypes.CLEAR_PERSONA_REFERENCIA: {
      return initialState;
    }

    default: {
      return state;
    }
  }
}

export { initialState };
